import { useEffect, useState } from "react";
import ScrollToTop from "react-scroll-to-top";

// ---------------------------------------------------------------------------
// RentToMortgagePage.jsx
//
// LAYOUT CHANGE: Previously rendered a single <RentToMortgageCalculator />
// component containing both inputs and results in one card.
//
// Now mirrors CalculationsPage.jsx two-column pattern exactly:
//   - Left col  (max-w-lg): RentToMortgageCalculator — form inputs
//   - Right col (max-w-sm): RentToMortgageResults    — live results
//   - Stacked on mobile (grid-cols-1), side-by-side on md+ (md:grid-cols-2)
//
// Previously:
//   import RentToMortgageCalculator from "../components/RentToMortgageCalculator";
//   ...
//   <div className="mx-auto w-full max-w-2xl">
//     <RentToMortgageCalculator />
//   </div>
//
// State (calculatorData + results) is owned here and passed down as props,
// so both columns stay in sync without needing Context or a "Calculate" button.
// ---------------------------------------------------------------------------

import Hero from "../components/Hero";
import RentToMortgageCalculator, {
  calculateRentToMortgage,
} from "../components/RentToMortgageCalculator";
import RentToMortgageResults from "../components/RentToMortgageResults";
// NEW: RentToMortgageChart added — grouped bar chart card below the two-column grid
// Previously: no chart component existed on this page
import RentToMortgageChart from "../components/RentToMortgageChart";
import removeScrollLockClasses from "../utils/removeScrollLockClasses";

const DEFAULT_CALCULATOR_DATA = {
  monthlyRent: 1750,
  interestRate: 5,
  termYears: 35,
};

function RentToMortgagePage() {
  const [calculatorData, setCalculatorData] = useState(DEFAULT_CALCULATOR_DATA);
  const [results, setResults] = useState([0, 0, 0, 0]);

  // Recalculate results whenever any input changes — no "Calculate" button needed
  useEffect(() => {
    const { monthlyRent, interestRate, termYears } = calculatorData;
    setResults(calculateRentToMortgage(monthlyRent, interestRate, termYears));
  }, [calculatorData]);

  const handleReset = () => {
    setCalculatorData(DEFAULT_CALCULATOR_DATA);
  };

  // Mirror CalculationsPage.jsx: clear scroll-lock classes on mount
  useEffect(() => {
    removeScrollLockClasses();
  }, []);

  return (
    <>
      <Hero
        content={
          <h2 className="text-center text-3xl font-bold text-white">
            Rent to Mortgage Calculator
          </h2>
        }
      />

      {/* ALIGNMENT APPROACH: all three elements share the same grid.
          Left and right card wrappers are w-full (fill their column).
          Chart uses col-span-2 so its edges match the outer edges of both
          columns exactly — guaranteed alignment with no mx-auto guesswork.
          Previously: various mx-auto / max-w combinations caused misalignment.
          Previously: <div className="mx-auto w-full max-w-2xl"><RentToMortgageCalculator /></div> */}
      <div className="px-4 pb-16 md:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* LEFT — Form inputs — w-full fills the left column */}
            {/* Previously: mx-auto w-full max-w-sm (card was narrower than column) */}
            <div className="w-full">
              <RentToMortgageCalculator
                calculatorData={calculatorData}
                setCalculatorData={setCalculatorData}
                onReset={handleReset}
              />
            </div>

            {/* RIGHT — Live results — w-full fills the right column */}
            {/* Previously: hidden md:block / mx-auto w-full max-w-lg */}
            <div className="w-full">
              <RentToMortgageResults results={results} onReset={handleReset} />
            </div>

            {/* CHART — col-span-2 spans both columns.
                Left edge aligns with left card. Right edge aligns with right card.
                Previously: outside the grid in various wrappers — caused misalignment. */}
            <div className="col-span-1 md:col-span-2">
              <RentToMortgageChart
                calculatorData={calculatorData}
                results={results}
              />
            </div>
          </div>
        </div>
      </div>

      <ScrollToTop smooth />
    </>
  );
}

export default RentToMortgagePage;
